import { ClassNames, DayPicker } from 'react-day-picker';
import { useCallback, useMemo, useState } from 'react';
import { Input } from 'app/agriculture/forms/components/Input/Input';
import { enAU } from 'date-fns/locale';
import { CalendarIcon, ClockIcon } from 'app/agriculture/icons';
import './DatePickerInput.scss';
import { formatDate, parse, isValid as isValidDate, isMatch, startOfDay } from 'date-fns';
import { tz, TZDate } from '@date-fns/tz';
import { useTimeZone } from 'app/agriculture/hooks/useTimeZone/useTimeZone';
import { SimpleGrid } from 'app/agriculture/components/SimpleGrid/SimpleGrid';
import { primaryColourMain } from 'app/agriculture/material';
import { RefCallBack } from 'react-hook-form';

const dayPickerClassNames: Partial<ClassNames> = {
    root: 'DatePicker',
    day: 'DatePicker__day',
    day_button: 'DatePicker__day-button',
    outside: 'DatePicker__day--outside-month',
    today: 'DatePicker__day--today',
    selected: 'DatePicker__day--selected',
    weekday: 'DatePicker__weekday',
    weekdays: 'DatePicker__weekdays',
    week: 'DatePicker__week',
    weeks: 'DatePicker__weeks',
    month: 'DatePicker__month',
    month_grid: 'DatePicker__month-grid',
    nav: 'DatePicker__navigation',
    button_previous: 'DatePicker__nav-button-previous',
    button_next: 'DatePicker__nav-button-next',
    chevron: 'DatePicker__nav-button-icon',
    month_caption: 'DatePicker__caption',
};

interface DatePickerProps {
    id?: string;
    initialValue?: Date;
    label: string;
    onChange: (date?: Date) => void;
    dateFormat?: string;
    timeFormat?: string;
    showTimeInput?: boolean;
    errorMessage?: string;
    ref?: RefCallBack;
}

const dateFormats = [
    'dd MM yyyy',
    'd MM yyyy',
    'dd MMM yyyy',
    'd MMM yyyy',
    'dd-MM-yyyy',
    'dd/MM/yyyy',
    'dd / MM / yyyy',
    'dd MM yy',
    'dd MMM yy',
    'd MM yy',
    'd MMM yy',
    'dd-MM-yy',
    'dd/MM/yy',
    'dd / MM / yy',
] as const;

function parseDate(date: string, { timeZone }: { timeZone?: string } = {}) {
    const format = dateFormats.find((format) => isMatch(date, format));
    return parse(date, format ?? dateFormats[0], new TZDate(new Date(), timeZone), {
        in: timeZone == null ? undefined : tz(timeZone),
    });
}

const timeFormats = ['hh:mm a', 'h:mm a', 'hh:mm aaa', 'h:mm aaa', 'HH:mm'] as const;

function parseTime(
    time: string,
    { timeZone, referenceDate }: { timeZone?: string; referenceDate?: Date } = {},
) {
    const format = timeFormats.find((format) => isMatch(time, format));
    return parse(
        time,
        format ?? timeFormats[0],
        referenceDate ?? new TZDate(new Date(), timeZone),
        {
            in: timeZone == null ? undefined : tz(timeZone),
        },
    );
}

export function DatePickerInput({
    id,
    initialValue,
    onChange,
    label,
    dateFormat = 'dd MMM yyyy',
    timeFormat = 'h:mm aaa',
    showTimeInput = false,
    errorMessage,
    ref,
}: DatePickerProps) {
    const { timeZone } = useTimeZone();

    // convert the initialValue to a TZDate, use now if there is no initialValue
    const initialDate = useMemo(
        () => new TZDate(initialValue ?? new Date(), timeZone),
        [initialValue, timeZone],
    );

    const [selectedDate, setSelectedDate] = useState<Date>(() => startOfDay(initialDate));

    const [month, setMonth] = useState(() => selectedDate);

    const [timeInputValue, setTimeInputValue] = useState<string>(
        showTimeInput ? formatDate(initialDate, timeFormat) : '0:00',
    );

    const [dateInputValue, setDateInputValue] = useState<string>(() =>
        formatDate(initialDate, dateFormat),
    );

    const handleChange = useCallback(
        ({ date = selectedDate, time = timeInputValue }: { date?: Date; time?: string }) => {
            const parsedDateTime = parseTime(time, {
                timeZone,
                referenceDate: date,
            });

            if (!isValidDate(parsedDateTime)) {
                onChange();
                return;
            }

            onChange(parsedDateTime);
        },
        [onChange, selectedDate, timeZone, timeInputValue],
    );

    const handleTimeInputChange = useCallback(
        (time: string) => {
            setTimeInputValue(time);
            handleChange({ time });
        },
        [handleChange],
    );

    const handleDateInputChange = useCallback(
        (dateInputValue: string) => {
            setDateInputValue(dateInputValue);
            const date = parseDate(dateInputValue, { timeZone });

            if (isValidDate(date)) {
                setSelectedDate(date);
                setMonth(date);
            }

            handleChange({ date });
        },
        [handleChange, timeZone],
    );

    const handleDayPickerSelect = useCallback(
        (date: Date) => {
            setSelectedDate(date);
            setDateInputValue(formatDate(date, dateFormat, { in: tz(timeZone) }));
            handleChange({ date });
        },
        [handleChange, timeZone, dateFormat],
    );

    const dateInput = (
        <Input
            id={id}
            ref={ref}
            type="text"
            label={label}
            value={dateInputValue}
            onChange={(e) => {
                handleDateInputChange(e.currentTarget.value);
            }}
            errorMessage={errorMessage}
            focusPopover={
                <>
                    <DayPicker
                        month={month}
                        onMonthChange={setMonth}
                        classNames={dayPickerClassNames}
                        mode="single"
                        selected={selectedDate}
                        locale={enAU}
                        timeZone={timeZone}
                        onSelect={handleDayPickerSelect}
                        showOutsideDays
                        required
                    />
                </>
            }
            adornment={{
                position: 'end',
                children: <CalendarIcon color={primaryColourMain} size={20} />,
            }}
        />
    );

    const timeInput = (
        <Input
            type="text"
            label="Time"
            value={timeInputValue}
            onChange={(e) => {
                handleTimeInputChange(e.currentTarget.value);
            }}
            adornment={{
                position: 'end',
                children: <ClockIcon color={primaryColourMain} size={20} />,
            }}
        />
    );

    if (!showTimeInput) return dateInput;

    return (
        <SimpleGrid columns={2}>
            {dateInput}
            {timeInput}
        </SimpleGrid>
    );
}
